// CycleTracker.jsx
import { Box, Stack, Typography, CircularProgress, TextField, Snackbar, Alert, IconButton } from "@mui/material";
import { useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar, PickersDay } from "@mui/x-date-pickers";
import { useNextMonthDisabled, usePreviousMonthDisabled, useUtils } from "@mui/x-date-pickers/internals";
import { useTheme, alpha } from "@mui/material/styles";
import { Plus, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/de";
import axios from "axios";

import { useCycleDataContext as useCycleData } from "../CycleDataContext";
import { useTranslation } from "../../../../language";
import { API_URL } from "../../../../constants";
import ButtonsMain from "../../../../components/Button/ButtonMain";

const MENSTRUAL_RED = "#c62828";
const MENSTRUAL_RED_TEXT_DARK = "#ef5350";

// Phase colors are the one functional accent in this screen — everything else
// stays neutral/glass. Each phase maps to an existing brand hue family (no new
// colors introduced): menstrual keeps its dedicated red, the other three reuse
// tertiary/quartiary/primary — families this screen doesn't otherwise touch —
// rather than MUI's stock success/warning roles, which this app's theme never
// actually overrides (they were silently rendering default MUI colors, not
// this app's palette).
function usePhaseColors() {
  const theme = useTheme();
  return {
    menstrual:  MENSTRUAL_RED,
    follicular: theme.palette.tertiary.main,
    ovulation:  theme.palette.quartiary.main,
    luteal:     theme.palette.primary.main,
  };
}

// Custom header: groups the prev/next month arrows right around the month
// label instead of MUI's default (label on the left, arrows pinned far right).
function CustomCalendarHeader(props) {
  const {
    currentMonth, onMonthChange, minDate, maxDate,
    disabled, disableFuture, disablePast, timezone,
    view, onViewChange, views,
  } = props;
  const utils = useUtils();

  const isNextDisabled = useNextMonthDisabled(currentMonth, { disableFuture, maxDate, timezone });
  const isPreviousDisabled = usePreviousMonthDisabled(currentMonth, { disablePast, minDate, timezone });

  const selectNextMonth = () => onMonthChange(utils.addMonths(currentMonth, 1), "left");
  const selectPreviousMonth = () => onMonthChange(utils.addMonths(currentMonth, -1), "right");

  const canSwitchView = views.length > 1 && !disabled;
  const toggleView = () => {
    if (!canSwitchView) return;
    onViewChange(views.find((v) => v !== view) || views[0]);
  };

  const monthLabel = utils.formatByString(currentMonth, utils.formats.month);
  const yearLabel = utils.formatByString(currentMonth, utils.formats.year);

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.5, mb: 0.5 }}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton size="small" onClick={selectPreviousMonth} disabled={isPreviousDisabled} sx={{ ml: -1 }}>
          <ChevronLeft size={20} />
        </IconButton>
        <Typography variant="h6" fontWeight={700} sx={{ textTransform: "capitalize" }}>{monthLabel}</Typography>
        <IconButton size="small" onClick={selectNextMonth} disabled={isNextDisabled}>
          <ChevronRight size={20} />
        </IconButton>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.25}
        onClick={toggleView}
        sx={{ cursor: canSwitchView ? "pointer" : "default" }}>
        <Typography variant="body2" fontWeight={600} color="text.secondary">{yearLabel}</Typography>
        {canSwitchView && <ChevronDown size={14} />}
      </Stack>
    </Stack>
  );
}

const BAR_HEIGHT = 26;

function CycleDay({ getPhaseForDate, phaseColors, ...props }) {
  const { day, outsideCurrentMonth, ...pickerProps } = props;
  const theme = useTheme();
  const dark        = theme.palette.mode === "dark";
  const result      = getPhaseForDate(day);
  const isToday     = day.isSame(dayjs(), "day");
  const baseColor   = result ? phaseColors[result.phase] : undefined;
  const isPredicted = result?.isPredicted ?? false;

  // Consecutive same-phase days within the same calendar row join into one
  // continuous bar instead of each day being its own separate pill. Today
  // is a full member of that bar too — it keeps the same background/border
  // as its neighbors, then gets a solid filled disc dropped on top to mark
  // it, Whoop-style, rather than breaking the bar's continuity.
  const samePhase = (other) => !!other && !!result && other.phase === result.phase && (other.isPredicted ?? false) === isPredicted;
  const prevDay = day.subtract(1, "day");
  const nextDay = day.add(1, "day");
  // A neighbor in an adjacent month renders as an invisible filler cell in
  // this grid — never join toward it, even if its phase happens to match,
  // otherwise the visible day loses its rounded end-cap for no visible
  // reason (e.g. day 31 flush against an invisible Sept 1).
  const sameGridMonth = (other) => other.isSame(day, "month");
  // Week starts on Monday, so Monday (1) is the leftmost column and Sunday (0) the rightmost.
  const joinsLeft  = result && day.day() !== 1 && sameGridMonth(prevDay) && samePhase(getPhaseForDate(prevDay));
  const joinsRight = result && day.day() !== 0 && sameGridMonth(nextDay) && samePhase(getPhaseForDate(nextDay));

  return (
    <PickersDay
      {...pickerProps}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      disableRipple
      // We draw our own today indicator (the disc below) — suppress MUI's
      // built-in today border so it doesn't double up as a second outline.
      disableHighlightToday
      sx={{
        fontWeight: 600,
        cursor: "default",
        borderRadius: 0,
        height: BAR_HEIGHT,
        ml: joinsLeft ? 0 : "2px",
        mr: joinsRight ? 0 : "2px",
        ...(!joinsLeft  && { borderTopLeftRadius: BAR_HEIGHT / 2, borderBottomLeftRadius: BAR_HEIGHT / 2 }),
        ...(!joinsRight && { borderTopRightRadius: BAR_HEIGHT / 2, borderBottomRightRadius: BAR_HEIGHT / 2 }),
        ...(baseColor && !isPredicted && {
          backgroundColor: alpha(baseColor, dark ? 0.55 : 0.5),
          border: `1px solid ${alpha(baseColor, dark ? 0.7 : 0.6)}`,
          ...(joinsLeft  && { borderLeft: "none" }),
          ...(joinsRight && { borderRight: "none" }),
          color: dark ? theme.palette.common.white : theme.palette.greydark.C900,
          "&:hover": { backgroundColor: alpha(baseColor, dark ? 0.55 : 0.5) },
        }),
        ...(baseColor && isPredicted && {
          backgroundColor: alpha(baseColor, dark ? 0.22 : 0.18),
          border: `1px solid ${alpha(baseColor, dark ? 0.4 : 0.32)}`,
          ...(joinsLeft  && { borderLeft: "none" }),
          ...(joinsRight && { borderRight: "none" }),
          color: theme.palette.text.secondary,
          "&:hover": { backgroundColor: alpha(baseColor, dark ? 0.22 : 0.18) },
        }),
        // Today — a solid filled disc dropped behind the day number (Whoop-style),
        // not a border/shape that would break the bar's continuity.
        ...(isToday && {
          position: "relative",
          zIndex: 1,
          color: theme.palette.common.white,
          "&::before": {
            content: '""',
            position: "absolute",
            zIndex: -1,
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            boxSizing: "border-box",
            width: BAR_HEIGHT, height: BAR_HEIGHT,
            borderRadius: "50%",
            backgroundColor: baseColor || theme.palette.greydark.C900,
            border: `2px solid ${dark ? theme.palette.common.white : theme.palette.background.paper}`,
            pointerEvents: "none",
          },
        }),
      }}
    />
  );
}

// Hero metric — cycle day shown as a circular progress ring (Whoop-style
// focal point), stroke colored by the current phase's functional accent.
// Bold tabular numeral in the center, so digit widths don't jitter as the
// day count changes. Plain SVG stroke-dasharray — no new chart dependency.
const RING_SIZE = 176;
const RING_STROKE = 12;

function CycleRing({ day, totalDays, phaseLabel, phaseColor }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const radius = (RING_SIZE - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = day != null && totalDays ? Math.min(Math.max(day / totalDays, 0), 1) : 0;
  const dashOffset = circumference * (1 - progress);

  return (
    <Box sx={{ position: "relative", width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}>
      <svg width={RING_SIZE} height={RING_SIZE} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={radius}
          fill="none"
          stroke={isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
          strokeWidth={RING_STROKE}
        />
        <circle
          cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={radius}
          fill="none"
          stroke={phaseColor || theme.palette.text.secondary}
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease" }}
        />
      </svg>
      <Box sx={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      }}>
        <Typography
          variant="number1"
          fontWeight={700}
          sx={{ lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
        >
          {day ?? "—"}
        </Typography>
        {phaseLabel && (
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ mt: 0.75, color: phaseColor, textAlign: "center", px: 1 }}
          >
            {phaseLabel}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function CycleTracker() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();
  const phaseColors = usePhaseColors();
  const menstrualColor = isDark ? MENSTRUAL_RED_TEXT_DARK : MENSTRUAL_RED;

  const { loading, activeCycle, predictedCycles, statistics, loadAll, getPhaseForDate } = useCycleData();
  const [viewingMonth, setViewingMonth] = useState(dayjs().startOf("month"));

  // New period flow
  const [newPeriodOpen, setNewPeriodOpen]   = useState(false);
  const [newPeriodDate, setNewPeriodDate]   = useState(dayjs().format("YYYY-MM-DD"));
  const [saving,        setSaving]          = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleNewPeriod = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/cycles/`, {
        cycle_start_date:     newPeriodDate,
        period_length:        statistics?.average_period_length  || activeCycle?.period_length  || 4,
        average_cycle_length: statistics?.average_cycle_length   || activeCycle?.average_cycle_length || 28,
        ovulation_start_day:  statistics?.average_ovulation_day  || activeCycle?.ovulation_start_day  || undefined,
      });
      setSnackbar({ open: true, message: t("cyc_notify_period_started"), severity: "success" });
      setNewPeriodOpen(false);
      setNewPeriodDate(dayjs().format("YYYY-MM-DD"));
      loadAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || t("cyc_notify_period_start_fail"), severity: "error" });
    } finally { setSaving(false); }
  };

  if (loading) {
    return <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>;
  }

  const today = dayjs().startOf("day");
  const cycleDay = activeCycle?.cycle_start_date
    ? today.diff(activeCycle.cycle_start_date, "day") + 1
    : null;
  const currentPhaseResult = getPhaseForDate(today);
  const currentPhaseColor = currentPhaseResult ? phaseColors[currentPhaseResult.phase] : null;
  const totalCycleDays = statistics?.average_cycle_length || activeCycle?.average_cycle_length || 28;
  const nextPredicted = predictedCycles?.[0];
  const daysUntilNextPeriod = nextPredicted?.cycle_start_date
    ? nextPredicted.cycle_start_date.diff(today, "day")
    : null;

  const calendarSlots = {
    day: (props) => (
      <CycleDay {...props} getPhaseForDate={getPhaseForDate} phaseColors={phaseColors} />
    ),
    calendarHeader: CustomCalendarHeader,
  };
  const calendarSx = {
    "& .MuiPickersDay-root": { fontWeight: 600 },
    "& .MuiDayCalendar-weekDayLabel": { fontWeight: 700, fontSize: "0.75rem" },
    // Pin every week row to the same height — otherwise a sparser row (e.g. a
    // trailing week with just one day) can render at a slightly different height.
    "& .MuiDayCalendar-weekContainer": { minHeight: BAR_HEIGHT },
  };
  const DOW_KEYS = ["cyc_dow_su", "cyc_dow_mo", "cyc_dow_tu", "cyc_dow_we", "cyc_dow_th", "cyc_dow_fr", "cyc_dow_sa"];
  const dayOfWeekFormatter = (date) => t(DOW_KEYS[date.day()]);

  // Glass shell recipe — same translucent/blur/border/inset-highlight values
  // already used elsewhere in this app (WelcomeGlassAccordion, HealthCockpit's
  // MobilePillarExplorer), just applied locally here rather than shared.
  const glassSx = {
    borderRadius: "28px",
    overflow: "hidden",
    bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
    boxShadow: isDark
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  };
  // Near-opaque inner layer — all text/data content lives on this, never
  // directly on the blurred `glassSx` fill behind it.
  const contentSx = {
    borderRadius: "20px",
    bgcolor: isDark ? alpha("#fff", 0.08) : "rgba(255,255,255,0.92)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.95)"}`,
  };
  // Buttons are controls, so — per the same rule — they get the glass
  // treatment themselves rather than sitting solid inside the content layer.
  const glassButtonSx = {
    px: "28px",
    color: isDark ? "common.white" : "greydark.C900",
    bgcolor: isDark ? alpha("#fff", 0.08) : alpha("#fff", 0.55),
    borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.7)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    "&:hover": {
      color: isDark ? "common.white" : "greydark.C900",
      bgcolor: isDark ? alpha("#fff", 0.13) : alpha("#fff", 0.7),
      borderColor: isDark ? "rgba(255,255,255,0.24)" : "rgba(255,255,255,0.85)",
      transform: "scale(1.04)",
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Box sx={{ width: "100%" }}>

        {/* Glass card — the calendar + cycle summary live inside here */}
        <Box sx={{ ...glassSx, p: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ ...contentSx, p: { xs: 1.5, sm: 2.5 } }}>

            {/* Hero metric — cycle day ring, the one focal stat on this screen */}
            {(cycleDay != null || daysUntilNextPeriod != null) && (
              <Box sx={{ mb: 2.5, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: "0.08em", fontWeight: 700, mb: 1 }}
                >
                  {t("cyc_summary_day_label")}
                </Typography>
                <CycleRing
                  day={cycleDay}
                  totalDays={totalCycleDays}
                  phaseLabel={currentPhaseResult ? t(`cyc_phase_${currentPhaseResult.phase}`) : null}
                  phaseColor={currentPhaseColor}
                />
                {daysUntilNextPeriod != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {t("cyc_summary_next_period_label")}: {daysUntilNextPeriod}{" "}
                    {t(daysUntilNextPeriod === 1 ? "cyc_days_singular" : "cyc_days_plural")}
                  </Typography>
                )}
              </Box>
            )}

            {/* Calendar — single month, same on every screen size */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <DateCalendar
                value={null}
                onChange={() => {}}
                referenceDate={viewingMonth}
                onMonthChange={(m) => setViewingMonth(dayjs(m).startOf("month"))}
                maxDate={dayjs().add(3, "month")}
                dayOfWeekFormatter={dayOfWeekFormatter}
                slots={calendarSlots}
                sx={calendarSx}
              />
            </Box>

            {/* Legend */}
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap"
              useFlexGap sx={{ mt: 2 }}>
              {Object.entries(phaseColors).map(([phase, color]) => (
                <Stack key={phase} direction="row" spacing={0.5} alignItems="center">
                  <Box sx={{
                    width: 14, height: 14, borderRadius: "50%",
                    bgcolor: alpha(color, 0.55),
                    border: `1px solid ${color}`,
                  }} />
                  <Typography variant="caption" fontWeight={600}>
                    {t(`cyc_phase_${phase}`)}
                  </Typography>
                </Stack>
              ))}
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box sx={{
                  width: 14, height: 14, borderRadius: "50%",
                  bgcolor: alpha(theme.palette.text.secondary, 0.12),
                  border: `1.5px dashed ${alpha(theme.palette.text.secondary, 0.4)}`,
                }} />
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {t("cyc_status_predicted")}
                </Typography>
              </Stack>
            </Stack>

          </Box>
        </Box>

        {/* New period — a control, so it floats below the card as its own
            glass element rather than living inside the solid content layer */}
        {activeCycle && (
          <Box sx={{ mt: 3 }}>
            {!newPeriodOpen ? (
              <Stack alignItems="center">
                <ButtonsMain
                  name={t("cyc_admin_new_period_btn")}
                  action={() => setNewPeriodOpen(true)}
                  startIcon={<Plus size={15} />}
                  sx={glassButtonSx}
                />
              </Stack>
            ) : (
              <Box sx={{
                p: 2, borderRadius: 3,
                border: `1px solid ${alpha(MENSTRUAL_RED, 0.3)}`,
                bgcolor: alpha(MENSTRUAL_RED, isDark ? 0.06 : 0.03),
              }}>
                <Typography variant="caption" fontWeight={700} display="block"
                  sx={{ color: menstrualColor, mb: 0.5 }}>
                  {t("cyc_admin_new_period_btn")}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  {t("cyc_admin_new_period_desc")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                  <TextField
                    type="date"
                    label={t("cyc_admin_period_start_date")}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                    value={newPeriodDate}
                    onChange={(e) => setNewPeriodDate(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <ButtonsMain name={t("cyc_cancel")} action={() => setNewPeriodOpen(false)} sx={glassButtonSx} />
                    <ButtonsMain
                      name={saving ? t("cyc_saving") : t("cyc_confirm")}
                      action={handleNewPeriod}
                      disabled={!newPeriodDate || saving}
                      sx={glassButtonSx}
                    />
                  </Stack>
                </Stack>
              </Box>
            )}
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Box>
    </LocalizationProvider>
  );
}
